import React, { useState, useEffect } from 'react';
import { CreditCard, ExternalLink, Loader2, CheckCircle, AlertCircle, DollarSign, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import './StripeConnectManagement.css';

const StripeConnectManagement = () => {
  const { user } = useAuth();
  const [accountStatus, setAccountStatus] = useState(null);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [creatingLink, setCreatingLink] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Load account status and balance
  const loadAccountData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      
      // Get account status
      const statusResponse = await fetch('/api/stripe-connect/account-status', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (statusResponse.ok) {
        const statusData = await statusResponse.json();
        setAccountStatus(statusData);
        
        // If user has an account, get balance
        if (statusData.has_account) {
          const balanceResponse = await fetch('/api/stripe-connect/balance', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (balanceResponse.ok) {
            const balanceData = await balanceResponse.json();
            setBalance(balanceData);
          }
        }
      } else {
        const errorData = await statusResponse.json();
        setError(errorData.error || 'Failed to load account data');
      }
    } catch (err) {
      console.error('Error loading account data:', err);
      setError('Failed to load account data');
    } finally {
      setLoading(false);
    }
  };

  // Create Stripe Connect account
  const createAccount = async () => {
    try {
      setCreatingAccount(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const response = await fetch('/api/stripe-connect/create-account', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setSuccess('Stripe Connect account created successfully!');
        await loadAccountData();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to create Stripe Connect account');
      }
    } catch (err) {
      console.error('Error creating account:', err);
      setError('Failed to create Stripe Connect account');
    } finally {
      setCreatingAccount(false);
    }
  };

  // Create onboarding link
  const createOnboardingLink = async () => {
    try {
      setCreatingLink(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const response = await fetch('/api/stripe-connect/create-onboarding-link', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          refresh_url: `${window.location.origin}/build/agentsettings?stripe=refresh`,
          return_url: `${window.location.origin}/build/agentsettings?stripe=success`
        })
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.url;
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to create onboarding link');
      }
    } catch (err) {
      console.error('Error creating onboarding link:', err);
      setError('Failed to create onboarding link');
    } finally {
      setCreatingLink(false);
    }
  };

  // Create account update link
  const createUpdateLink = async () => {
    try {
      setCreatingLink(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const response = await fetch('/api/stripe-connect/create-update-link', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          refresh_url: `${window.location.origin}/build/agentsettings?stripe=refresh`,
          return_url: `${window.location.origin}/build/agentsettings?stripe=success`
        })
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.url;
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to create update link');
      }
    } catch (err) {
      console.error('Error creating update link:', err);
      setError('Failed to create update link');
    } finally {
      setCreatingLink(false);
    }
  };

  // Format currency
  const formatCurrency = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="status-badge status-active"><CheckCircle size={16} /> Active</span>;
      case 'pending':
        return <span className="status-badge status-pending"><AlertCircle size={16} /> Pending</span>;
      case 'not_connected':
        return <span className="status-badge status-inactive"><AlertCircle size={16} /> Not Connected</span>;
      default:
        return <span className="status-badge status-inactive"><AlertCircle size={16} /> Unknown</span>;
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadAccountData();
    
    // Check for Stripe redirect parameters
    const urlParams = new URLSearchParams(window.location.search);
    const stripeParam = urlParams.get('stripe');
    
    if (stripeParam === 'success') {
      setSuccess('Stripe Connect account setup completed successfully!');
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (stripeParam === 'refresh') {
      setSuccess('Account updated successfully!');
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  if (loading) {
    return (
      <div className="stripe-connect-management">
        <div className="loading-container">
          <Loader2 className="spinner" />
          <p>Loading Stripe Connect account...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="stripe-connect-management">
      <div className="connect-header">
        <h3>Stripe Connect Account</h3>
        <p>Manage your Stripe Connect account to receive payments from agent interactions.</p>
      </div>

      {error && (
        <div className="error-message">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {success && (
        <div className="success-message">
          <CheckCircle size={16} />
          {success}
        </div>
      )}

      {!accountStatus?.has_account ? (
        <div className="no-account-section">
          <div className="no-account-card">
            <CreditCard size={48} />
            <h4>No Stripe Connect Account</h4>
            <p>Create a Stripe Connect Express account to start receiving payments from your agents.</p>
            <button
              onClick={createAccount}
              disabled={creatingAccount}
              className="create-account-button"
            >
              {creatingAccount ? (
                <>
                  <Loader2 className="spinner" />
                  Creating Account...
                </>
              ) : (
                <>
                  <CreditCard size={16} />
                  Create Stripe Connect Account
                </>
              )}
            </button>
          </div>
        </div>
      ) : (
        <div className="account-section">
          <div className="account-status-card">
            <div className="account-header">
              <h4>Account Status</h4>
              <button onClick={loadAccountData} className="refresh-button">
                <RefreshCw size={16} />
              </button>
            </div>
            
            <div className="status-info">
              {getStatusBadge(accountStatus.status)}
              <div className="status-details">
                <div className="status-item">
                  <span className="label">Charges Enabled:</span>
                  <span className={`value ${accountStatus.charges_enabled ? 'enabled' : 'disabled'}`}>
                    {accountStatus.charges_enabled ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="status-item">
                  <span className="label">Payouts Enabled:</span>
                  <span className={`value ${accountStatus.payouts_enabled ? 'enabled' : 'disabled'}`}>
                    {accountStatus.payouts_enabled ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="status-item">
                  <span className="label">Details Submitted:</span>
                  <span className={`value ${accountStatus.details_submitted ? 'enabled' : 'disabled'}`}>
                    {accountStatus.details_submitted ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </div>

            {accountStatus.status !== 'active' && (
              <div className="account-actions">
                <button
                  onClick={createOnboardingLink}
                  disabled={creatingLink}
                  className="onboarding-button"
                >
                  {creatingLink ? (
                    <>
                      <Loader2 className="spinner" />
                      Redirecting...
                    </>
                  ) : (
                    <>
                      <ExternalLink size={16} />
                      Complete Account Setup
                    </>
                  )}
                </button>
              </div>
            )}

            {accountStatus.status === 'active' && (
              <div className="account-actions">
                <button
                  onClick={createUpdateLink}
                  disabled={creatingLink}
                  className="update-button"
                >
                  {creatingLink ? (
                    <>
                      <Loader2 className="spinner" />
                      Redirecting...
                    </>
                  ) : (
                    <>
                      <ExternalLink size={16} />
                      Update Account Details
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          {balance && (
            <div className="balance-card">
              <div className="balance-header">
                <h4>Account Balance</h4>
                <DollarSign size={20} />
              </div>
              
              <div className="balance-info">
                <div className="balance-item">
                  <span className="label">Available:</span>
                  <span className="value available">{formatCurrency(balance.available)}</span>
                </div>
                <div className="balance-item">
                  <span className="label">Pending:</span>
                  <span className="value pending">{formatCurrency(balance.pending)}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="connect-info">
        <h4>How Stripe Connect Works</h4>
        <ul>
          <li>Create a Stripe Connect Express account to receive payments</li>
          <li>Complete the onboarding process to enable payouts</li>
          <li>Earn 85% of revenue from agent interactions</li>
          <li>Receive automatic daily payouts to your bank account</li>
          <li>Manage your account details and tax information through Stripe</li>
        </ul>
      </div>
    </div>
  );
};

export default StripeConnectManagement;
